import { debounce } from 'lodash'

import authenticationService from 'Services/AuthenticationService'
import policyStructureService from 'Services/PolicyStructureService'
import permissionService from 'Services/PermissionService'

// initial state
const state = {
  isLoading: false,
  credentials: {
    accessToken: null,
    idToken: null,
    refreshToken: null,
    validUntilUtc: null
  },

  permissions: [],
  policy: null,

  userInfo: null
}

const setStateDebounce = debounce(func => func(), 250)

// getters
const getters = {
  isAuthorized: state => () => Boolean(state.credentials != null && state.credentials.accessToken != null && Date.now() < new Date(state.credentials.validUntilUtc))
}

// actions
const actions = {
  authorize: ({ dispatch }, code) => {
    return dispatch('authorizeOrRefresh', { action: authenticationService.login(code) })
  },

  refresh: ({ dispatch }, refreshToken) => {
    return dispatch('authorizeOrRefresh', { action: authenticationService.refresh(refreshToken) })
  },

  authorizeOrRefresh: ({ state, commit, dispatch }, { action }) => {
    setStateDebounce(() => { commit('setAuthorizationProperty', { property: 'isLoading', value: true }) })

    return new Promise((resolve, reject) => {
      action.then(credentials => {
        const userInfo = new Promise(resolve => { state.userInfo === null ? dispatch('getUserInfo', credentials).then(resolve) : resolve() })
        const policy = new Promise(resolve => { state.policy === null ? dispatch('getUserPolicy', credentials).then(resolve) : resolve() })

        Promise.all([userInfo, policy]).then(() => {
          commit('setAuthorizationProperty', { property: 'credentials', value: Object.assign(state.credentials, credentials, { validUntilUtc: new Date().setSeconds(credentials.expiresIn) }) })

          resolve(state.credentials)
        })
      }).catch(error => {
        dispatch('unauthorize')

        reject(error)
      }).finally(() => {
        if (state.isLoading) {
          commit('setAuthorizationProperty', { property: 'isLoading', value: false })
        } else {
          setStateDebounce.cancel()
        }
      })
    })
  },

  unauthorize: ({ commit }) => {
    commit('setAuthorizationProperty', { property: 'credentials', value: {} })
    commit('setAuthorizationProperty', { property: 'permissions', value: [] })
    commit('setAuthorizationProperty', { property: 'policy', value: null })
    commit('setAuthorizationProperty', { property: 'userInfo', value: null })
  },

  getUserInfo: ({ state, commit }, { accessToken }) => {
    return new Promise((resolve, reject) => {
      authenticationService.userInfo(accessToken).then((userInfo) => {
        commit('setAuthorizationProperty', { property: 'userInfo', value: { givenName: '', familyName: '', ...userInfo } })
        resolve()
      }).catch(error => {
        reject(error)
      })
    })
  },

  getUserPolicy: ({ commit }, { accessToken }) => {
    return new Promise((resolve, reject) => {
      policyStructureService.get(accessToken).then(policy => {
        commit('setAuthorizationProperty', { property: 'policy', value: policy })
        resolve()
      }).catch(error => {
        reject(error)
      })
    })
  },

  getPermissions: ({ commit }, context) => {
    return new Promise((resolve, reject) => {
      permissionService.getPermissions(context).then(permissions => {
        commit('setAuthorizationProperty', { property: 'permissions', value: permissions })
        resolve(permissions)
      }).catch(error => {
        reject(error)
      })
    })
  }
}

// mutations
const mutations = {
  setAuthorizationProperty (state, { property, value }) {
    state[property] = value
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
